import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, Mail, Phone, Copy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import { PAGES, PAGE_SLUGS, PAGE_ORDER } from "@/i18n/pages";
import { BLOG, ARTICLE_SLUGS, ARTICLE_ORDER } from "@/i18n/blog";
import { openCookieSettings } from "@/components/CookieConsent";
import { Overline } from "@/components/landing/primitives";

const ContactFooter = () => {
  const { t, lang } = useLang();
  const c = t.contact;

  const footerSolutions = PAGE_ORDER.map((k) => ({
    key: k,
    to: `/${PAGE_SLUGS[k]}`,
    label: PAGES[lang][k].h1,
  }));
  const footerArticles = ARTICLE_ORDER.map((k) => ({
    key: k,
    to: `/blog/${ARTICLE_SLUGS[k]}`,
    label: BLOG[lang].articles[k].title,
  }));

  const cards = [
    {
      key: "whatsapp",
      label: c.whatsapp,
      value: CONTACT.phone,
      href: `https://wa.me/${CONTACT.whatsapp}`,
      Icon: MessageCircle,
      accent: "green",
      external: true,
    },
    {
      key: "email",
      label: c.email,
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      Icon: Mail,
      accent: "red",
      copy: CONTACT.email,
    },
    {
      key: "phone",
      label: c.phone,
      value: CONTACT.phone,
      href: `tel:${CONTACT.phoneRaw}`,
      Icon: Phone,
      accent: "green",
    },
  ];

  const copyEmail = (e, val) => {
    e.preventDefault();
    navigator.clipboard?.writeText(val);
    toast.success(c.copied, { description: val });
  };

  return (
    <footer
      id="contact"
      className="relative overflow-hidden border-t border-white/10 bg-[#0A0A0A] px-5 py-24 md:px-10 md:py-32"
      data-testid="contact-footer"
    >
      <div className="pointer-events-none absolute -bottom-40 left-1/2 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-brand-red/10 blur-[160px]" />

      <div className="relative mx-auto max-w-[1400px]">
        <Overline>{c.overline}</Overline>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 font-display text-6xl font-black leading-[0.9] text-white md:text-[9rem]"
        >
          {c.title}
        </motion.h2>
        <p className="mt-6 max-w-lg text-base text-white/60 md:text-lg">{c.subtitle}</p>

        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {cards.map((card, i) => (
            <motion.a
              key={card.key}
              href={card.href}
              target={card.external ? "_blank" : undefined}
              rel={card.external ? "noopener noreferrer" : undefined}
              onClick={card.copy ? (e) => copyEmail(e, card.copy) : undefined}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className={`group flex flex-col justify-between border border-white/10 bg-[#121212] p-8 transition-all duration-300 hover:-translate-y-1 ${
                card.accent === "red"
                  ? "hover:border-brand-red hover:shadow-glow-red"
                  : "hover:border-brand-green hover:shadow-glow-green"
              }`}
              data-testid={`contact-card-${card.key}`}
            >
              <div className="flex items-center justify-between">
                <card.Icon
                  className={`h-8 w-8 ${
                    card.accent === "red" ? "text-brand-red" : "text-brand-green"
                  }`}
                />
                {card.copy ? (
                  <Copy className="h-4 w-4 text-white/30 group-hover:text-white/70" />
                ) : (
                  <ArrowUpRight className="h-5 w-5 text-white/30 group-hover:text-white/70" />
                )}
              </div>
              <div className="mt-16">
                <div className="font-mono text-[11px] uppercase tracking-widest text-white/40">
                  {card.label}
                </div>
                <div className="mt-1 font-display text-xl font-bold text-white">
                  {card.value}
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Sitewide quick links for internal linking / SEO */}
        <nav className="mt-20 grid gap-8 border-t border-white/10 pt-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="footer-links">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">
              {t.solutions.overline}
            </div>
            <ul className="mt-5 space-y-3">
              {footerSolutions.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/60 transition-colors hover:text-white"
                    data-testid={`footer-link-${l.key}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">
              {t.nav.blog}
            </div>
            <ul className="mt-5 space-y-3">
              {footerArticles.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/60 transition-colors hover:text-white"
                    data-testid={`footer-article-${l.key}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">
              {t.nav.contact}
            </div>
            <ul className="mt-5 space-y-3 text-sm text-white/60">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-white">
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a href={`tel:${CONTACT.phoneRaw}`} className="transition-colors hover:text-white">
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <Link to="/blog" className="transition-colors hover:text-white" data-testid="footer-link-blog">
                  {t.nav.blog}
                </Link>
              </li>
            </ul>
          </div>
        </nav>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <div>
            <span className="font-display text-2xl font-black text-white">
              TUNINGFILES<span className="text-brand-green">.hu</span>
            </span>
            <p className="mt-2 max-w-md text-sm text-white/50">{t.footer.tagline}</p>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-white/40">
            © {new Date().getFullYear()} TuningFiles.hu — {t.footer.rights}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-white/50">
            <Link to="/impressum" className="transition-colors hover:text-white" data-testid="footer-impressum">
              {t.footer.impressum}
            </Link>
            <Link to="/datenschutz" className="transition-colors hover:text-white" data-testid="footer-privacy">
              {t.footer.privacy}
            </Link>
            <button
              onClick={openCookieSettings}
              className="uppercase tracking-widest transition-colors hover:text-white"
              data-testid="footer-cookie-settings"
            >
              {t.footer.cookieSettings}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ContactFooter;
